import { useEffect, useMemo, useRef } from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import { tokens } from "../theme";
import {
  MIN_HOUR_WIDTH,
  hourLabelStep,
  useElementWidth,
} from "../utils/stripScale";
import type { SignageTimeline as Timeline } from "@shared/types/signage";

/**
 * The 24-hour playout strip for one screen.
 *
 * A scrolling hour ruler with the resolved content intervals laid over it, the
 * campaign breaks marked underneath, and a playhead at "now". Geometry is
 * derived from real timestamps rather than from indexes, so an interval that
 * starts at 09:17 sits at 09:17 - the point of the strip is that an operator
 * can see the shape of the day and spot a gap.
 *
 * Everything is drawn from theme tokens, so it inverts correctly in dark mode.
 */

export { MIN_HOUR_WIDTH } from "../utils/stripScale";

/**
 * The vertical metrics, shared by every row on a card.
 *
 * Exported for the same reason `PLAYHEAD_SX` is: the campaign lanes sit
 * directly under the content strip and their gridlines, playhead and label
 * column all start below the ruler. Two copies of "how tall is a ruler" is two
 * rows that stop lining up the moment one of them is adjusted - and adjusting
 * them is exactly what this pass did.
 */
/**
 * The block label's own geometry.
 *
 * Shared by the FLOW gap and the two sticky offsets, because those have to
 * agree: the thumbnail pins at `EDGE`, the text pins clear of it, and the flow
 * gap is the same distance so nothing shifts as a block scrolls under the
 * viewport edge.
 */
const BLOCK_THUMB = 64;
const BLOCK_LABEL_EDGE = 8;
const BLOCK_LABEL_GAP = 12;

export const RULER_HEIGHT = 34;
/** Where a content block starts, and how tall it is. */
const BLOCK_TOP = RULER_HEIGHT + 22;
const BLOCK_HEIGHT = 92;
/**
 * The forced-channel row, under the schedule it is masking.
 *
 * Under, not instead of: an operator has to be able to see WHAT the takeover
 * is covering, otherwise "why is the lunch promo not running" has no answer on
 * this screen. The schedule stays drawn and goes quiet; the override sits below
 * it at full strength.
 */
const OVERRIDE_GAP = 10;
const OVERRIDE_HEIGHT = 84;
/** Smaller than the schedule row's, so the takeover card is not the loudest
 *  thing on a card whose subject is the week. */
const OVERRIDE_THUMB = 56;
const OVERRIDE_TOP = BLOCK_TOP + BLOCK_HEIGHT + OVERRIDE_GAP;

/** The fixed left rail carrying the now-playing summary. */
export const RAIL_WIDTH = 240;

/**
 * The "now" line, shared by every row on a card.
 *
 * Exported rather than repeated so the two strips cannot drift into two
 * different markers for the same instant - which is exactly what happened
 * first time round: the content strip grew rotated squares and the lanes got
 * a bare line with no ends at all, on one card, for one moment in time.
 *
 * Round ends, not the rotated squares that were here before. A caret points -
 * it says "over there", which is what you want on a marker that refers to
 * something beside it. This one marks the instant it sits on, and a dot is the
 * shape that says "here".
 */
export const PLAYHEAD_SX = {
  position: "absolute" as const,
  width: "2px",
  bgcolor: tokens.brand,
  pointerEvents: "none" as const,
  "&::before, &::after": {
    content: '""',
    position: "absolute" as const,
    left: "-3px",
    width: 8,
    height: 8,
    borderRadius: "50%",
    bgcolor: tokens.brand,
  },
  "&::before": { top: -4 },
  "&::after": { bottom: -4 },
};

/** The strip's own origin and span, so a sibling row can share its ruler. */
export function stripGeometry(generatedAt: string, horizonEndsAt: string) {
  const start = Date.parse(generatedAt);
  const end = Date.parse(horizonEndsAt);
  const originMs = Math.floor(start / 3_600_000) * 3_600_000;
  const hours = Math.max(1, Math.ceil((end - originMs) / 3_600_000));
  return {
    originMs,
    hours,
    pctOf: (ms: number) => ((ms - originMs) / 3_600_000 / hours) * 100,
  };
}

export interface SignageTimelineProps {
  timeline: Timeline;
  /** Rendered position of the playhead. Passed in so every card on the page
   *  ticks off one clock rather than each starting its own interval. */
  now: Date;
  /** Left rail content - the now-playing block. */
  rail?: React.ReactNode;
  /** Resolve a playlist id to a display name. */
  playlistName: (id: string) => string;
  /** First frame of a playlist, for the block thumbnail. */
  playlistThumb: (id: string) => string | null;
  /**
   * The screen's manual takeover, when one is running.
   *
   * Passed in rather than read off the blocks, because the resolver CLAMPS an
   * open-ended override to the end of the horizon - so a block alone cannot
   * tell "until 18:00" from "until somebody changes it back", and those are
   * very different things to print on a wall's card.
   */
  override?: { until: string | null } | null;
  /**
   * Campaign lanes, drawn UNDER the content on the same ruler.
   *
   * Passed in rather than rendered here because the lanes are about campaigns
   * and this component is about content - but they share a strip, and that is
   * the point. As two separate cards they read as two unrelated pictures of
   * different things; sharing the ruler, the scroll and the playhead makes
   * "which campaign is eligible while THAT playlist is on" a thing you can see
   * by looking down a line.
   *
   * Receives the geometry so a lane can position itself identically.
   */
  /**
   * Drop the outer border and radius, for a strip nested inside a bigger card.
   *
   * The campaign lanes sit directly under this on the SAME ruler, and two
   * bordered boxes stacked read as two unrelated pictures of different things
   * - which is the note that started this. One card, two rows.
   */
  flush?: boolean;
  /**
   * Move with the campaign lanes below.
   *
   * The two are one picture of one day; scrolled apart, the vertical line that
   * connects them lies. See `useLinkedScroll`.
   */
  scrollSync?: (el: HTMLElement | null) => void;
}

/** What a lane needs to sit on the same ruler as the content above it. */
export interface StripGeometry {
  /** Percentage across the strip for an instant. */
  pctOf: (ms: number) => number;
  originMs: number;
  hours: number;
}

/**
 * Wall-clock time AT THE SITE, not in the viewer's browser.
 *
 * Every instant on a timeline is real UTC, so formatting it with the browser's
 * own offset silently retimes the whole day for anyone managing a location in
 * another zone - a chain operator in Stockholm would read a London site's
 * 09:00 block as 10:00. The premise timezone travels on the timeline for
 * exactly this reason; it is the only correct thing to format against.
 */
function makeClock(timeZone: string) {
  const fmt = new Intl.DateTimeFormat("en-GB", {
    timeZone,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  return (iso: string | number) => {
    const parts = Object.fromEntries(
      fmt.formatToParts(new Date(iso)).map((p) => [p.type, p.value])
    ) as Record<string, string>;
    const hour = parts.hour === "24" ? "00" : parts.hour;
    return `${hour}:${parts.minute}`;
  };
}

export default function SignageTimeline({
  timeline,
  now,
  rail,
  playlistName,
  playlistThumb,
  override,
  flush = false,
  scrollSync,
}: SignageTimelineProps) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const clock = useMemo(
    () => makeClock(timeline.timezone || "UTC"),
    [timeline.timezone]
  );

  // Tick origin is floored to a whole UTC hour, so ticks are exactly an hour
  // apart everywhere. Labels are then rendered in the SITE's zone - which is
  // why they print real minutes rather than a hardcoded ".00": a location on a
  // half-hour offset (India, Nepal) genuinely sits at :30 past, and printing
  // ".00" there would be a lie drawn to the pixel.
  // ONE definition of the ruler, shared with the campaign lanes below - see
  // `stripGeometry`. Computed inline here once meant two rows deriving the
  // same origin separately, which is how they drift apart.
  const { originMs, hours, pctOf } = useMemo(
    () => stripGeometry(timeline.generatedAt, timeline.horizonEndsAt),
    [timeline.generatedAt, timeline.horizonEndsAt]
  );

  /*
   * Read off the RESOLVED blocks, not off the assignment.
   *
   * An override can exist on the assignment and still be showing nothing here
   * - it expired an hour ago, or its window has not opened yet, or the
   * playlist it names has been deleted. The strip must draw what is actually
   * on, so the source of truth is the block the resolver emitted.
   */
  /*
   * ACTIVE NOW, not merely present in the window.
   *
   * The strip is anchored to local midnight, so a takeover that ran from 08:48
   * to 11:48 is still inside today's 24 hours at teatime - and the row went on
   * announcing "forced channel active" over a wall that had gone back to its
   * schedule an hour before. The label is a claim about the present tense, so
   * the row has to be too.
   *
   * A takeover scheduled for LATER is not active either: it has not taken
   * anything over yet, and drawing it as though it had would be the same lie
   * pointed the other way.
   */
  const overrideBlocks = useMemo(() => {
    const at = now.getTime();
    return timeline.content.filter(
      (c) =>
        c.source === "override" &&
        Date.parse(c.startsAt) <= at &&
        at < Date.parse(c.endsAt)
    );
  }, [timeline.content, now]);
  const hasOverride = overrideBlocks.length > 0;

  /*
   * The schedule row draws the week UNCUT.
   *
   * `content` is cut around the takeover because that is what the player
   * plays, and drawing that here punched a hole in the operator's week - the
   * plan appeared to stop and restart around something sitting on a different
   * row. `scheduledContent` is the same week with no takeover applied, which
   * is what "faded underneath" is supposed to mean.
   *
   * Falls back to `content` when the resolver did not send it, which is the
   * ordinary no-override case where the two are the same thing anyway.
   */
  /*
   * The planned week, UNBROKEN, always.
   *
   * Never `content` here. `content` is cut around a takeover and carries the
   * takeover's own block, so falling back to it put a forced channel INSIDE
   * the planned row and split the plan around it - which says the week is
   * something it is not. The plan is the plan; anything forced on top of it
   * belongs on the row below, and only while it is running.
   */
  const scheduleRow = useMemo(
    () =>
      (timeline.scheduledContent ?? timeline.content).filter(
        (c) => c.source !== "override"
      ),
    [timeline.scheduledContent, timeline.content]
  );

  /*
   * The day FITS, and re-fits when the width changes.
   *
   * A fixed hour column meant a 24-hour strip was always 1296px, so zooming
   * changed how much of a fixed-size thing you saw rather than re-laying it
   * out. What actually needs room is the LABEL, so the labels thin out and the
   * day spans whatever width it is given.
   */
  const stripWidth = useElementWidth(scrollRef);
  const labelStep = hourLabelStep(stripWidth, hours);

  const minWidth = hours * MIN_HOUR_WIDTH;
  const playheadPct = pctOf(now.getTime());

  // Keep "now" in view on first paint. Without this a 24h strip opens at
  // midnight and the operator has to hunt for the live edge.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollLeft = Math.max(
      0,
      (playheadPct / 100) * el.scrollWidth - el.clientWidth / 3
    );
    // Only on mount / when the screen changes - not on every clock tick, which
    // would yank the viewport away from an operator who has scrolled.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeline.screenId]);

  return (
    <Box
      sx={{
        display: "flex",
        ...(flush
          ? {}
          : { border: `1px solid ${tokens.divider}`, borderRadius: "12px" }),
        overflow: "hidden",
        bgcolor: tokens.surface,
      }}
    >
      {/* Left rail - pinned, never scrolls with the strip. */}
      <Box
        sx={{
          width: RAIL_WIDTH,
          flexShrink: 0,
          borderRight: `1px solid ${tokens.divider}`,
          bgcolor: tokens.brandTintSoft,
          p: 2.5,
        }}
      >
        {/*
          The hardcoded LIVE dot that used to sit here is GONE.
          It was always on, whatever the panel was doing, which made it the
          most confident thing on a card describing a dark screen - and it sat
          directly above the real badge the rail now supplies, so a card could
          show LIVE and OFFLINE an inch apart.
        */}
        {rail}
      </Box>

      {/* Scrolling strip. */}
      <Box
        ref={(el: HTMLDivElement | null) => {
          scrollRef.current = el;
          scrollSync?.(el);
        }}
        sx={{
          flex: 1,
          // ⛔ `minWidth: 0`, and the whole strip depends on it.
          //
          // A flex item defaults to `min-width: auto`, which means it REFUSES
          // to shrink below its content's intrinsic width. The inner box
          // carries `minWidth: hours * MIN_HOUR_WIDTH` so a day never squashes
          // to something unreadable - and without this line that floor is
          // inherited by the scroll container itself. It then grows past the
          // card instead of scrolling inside it, and the card's `overflow:
          // hidden` silently cuts the day off: no scrollbar, just a timeline
          // that stops at 16:00 and does not say why.
          //
          // Only visible when the viewport is narrower than the floor, which
          // is why it survived every desktop test and appeared on ctrl-+.
          minWidth: 0,
          overflowX: "auto",
          overflowY: "hidden",
          position: "relative",
        }}
      >
        <Box
          sx={{
            width: "100%",
            minWidth,
            position: "relative",
            // The content block is 76px from top 44; lanes hang below it and
            // the strip has to grow for them or they are clipped by the card.
            minHeight:
              (hasOverride
                ? OVERRIDE_TOP + OVERRIDE_HEIGHT
                : BLOCK_TOP + BLOCK_HEIGHT) + 18,
          }}
        >
          {/* Hour ruler. */}
          <Box
            sx={{
              height: RULER_HEIGHT,
              position: "sticky",
              top: 0,
              bgcolor: tokens.canvas,
              borderBottom: `1px solid ${tokens.divider}`,
            }}
          >
            {Array.from({ length: hours }, (_, i) => i)
              .filter((i) => i % labelStep === 0)
              .map((i) => (
                <Typography
                  key={i}
                  sx={{
                    position: "absolute",
                    left: `${(i / hours) * 100}%`,
                    ml: "6px",
                    top: 8,
                    fontSize: 10,
                    fontWeight: 500,
                    letterSpacing: "0.2px",
                    color: tokens.inkFaint,
                  }}
                >
                  {clock(originMs + i * 3_600_000).replace(":", ".")}
                </Typography>
              ))}
          </Box>

          {/* Hour gridlines. */}
          {Array.from({ length: hours }, (_, i) => i)
            .filter((i) => i % labelStep === 0)
            .map((i) => (
              <Box
                key={i}
                sx={{
                  position: "absolute",
                  left: `${(i / hours) * 100}%`,
                  top: RULER_HEIGHT,
                  bottom: 0,
                  width: "1px",
                  bgcolor: tokens.divider,
                  opacity: 0.6,
                }}
              />
            ))}

          {/* Content intervals. The takeover is drawn on its own row below. */}
          {scheduleRow.map((c) => {
            const left = pctOf(Date.parse(c.startsAt));
            const right = pctOf(Date.parse(c.endsAt));
            const thumb = playlistThumb(c.playlistId);
            return (
              <Tooltip
                key={`${c.startsAt}-${c.playlistId}`}
                title={`${playlistName(c.playlistId)} · ${clock(c.startsAt)}–${clock(
                  c.endsAt
                )} · ${c.source}`}
              >
                <Box
                  sx={{
                    position: "absolute",
                    left: `${left}%`,
                    width: `${Math.max(0.1, right - left)}%`,
                    top: BLOCK_TOP,
                    height: BLOCK_HEIGHT,
                    borderRadius: "8px",
                    // An override is the operator's manual takeover, so it is
                    // the one that has to look different from the plan.
                    /*
                     * Quiet by RESTYLING, not by `opacity`.
                     *
                     * Fading the whole block took an already-pale tint to
                     * almost white and the label with it - the row was legible
                     * only as a shape. A masked block keeps its full contrast
                     * and changes its colours: neutral ground, grey text, the
                     * accent bar dropped to a divider. It reads as "this is
                     * the week, and it is not what is on right now".
                     */
                    bgcolor: hasOverride
                      ? tokens.rowAlt
                      : c.source === "override"
                        ? tokens.brandActive
                        : tokens.brandTintSoft,
                    border: `1px solid ${tokens.divider}`,
                    borderLeft: `3px solid ${
                      hasOverride ? tokens.divider : tokens.brand
                    }`,
                    display: "flex",
                    alignItems: "center",
                    gap: `${BLOCK_LABEL_GAP}px`,
                    px: 1,
                    overflow: "hidden",
                  }}
                >
                  {/*
                    STICKY, because a block is usually wider than the window.
                    A day-long interval starts at 00:00 and its label sat at
                    that edge - so an operator scrolled to the afternoon saw a
                    long blank bar and no idea what was on it. Pinning the
                    label to the left of the VIEWPORT keeps it readable
                    wherever the strip is scrolled, while the block itself
                    still spans its real hours.
                  */}
                  {/*
                    ALWAYS a square, poster or not.
                    Half the library has no artwork upstream, and a block that
                    simply omits the image starts its text at a different place
                    from the block above it - so a row of channels reads as
                    ragged rather than as a list.
                  */}
                  <Box
                    {...(thumb
                      ? { component: "img" as const, src: thumb, alt: "" }
                      : {})}
                    sx={{
                      width: BLOCK_THUMB,
                      height: BLOCK_THUMB,
                      flexShrink: 0,
                      borderRadius: "6px",
                      objectFit: "cover",
                      position: "sticky",
                      left: `${BLOCK_LABEL_EDGE}px`,
                      ...(thumb
                        ? { opacity: hasOverride ? 0.55 : 1 }
                        : {
                            bgcolor: tokens.divider,
                            opacity: hasOverride ? 0.5 : 0.7,
                          }),
                    }}
                  />
                  {/*
                    Its own sticky offset, CLEARING the thumbnail.
                    Both were pinned at `left: 8`, so the moment the strip
                    scrolled they stuck to the same place and the name landed
                    on top of a 64px image - `gap` only separates them while
                    they are still in normal flow, which is exactly when the
                    problem is invisible.
                  */}
                  <Box
                    sx={{
                      minWidth: 0,
                      position: "sticky",
                      left: `${BLOCK_LABEL_EDGE + BLOCK_THUMB + BLOCK_LABEL_GAP}px`,
                    }}
                  >
                    <Typography
                      noWrap
                      sx={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: hasOverride ? tokens.inkMuted : tokens.ink,
                      }}
                    >
                      {playlistName(c.playlistId)}
                    </Typography>
                    <Typography
                      noWrap
                      sx={{
                        fontSize: 11,
                        color: hasOverride ? tokens.inkFaint : tokens.inkMuted,
                      }}
                    >
                      {clock(c.startsAt)} – {clock(c.endsAt)}
                    </Typography>
                  </Box>
                </Box>
              </Tooltip>
            );
          })}

          {/* -- the forced channel, on its own row under the schedule ------ */}
          {hasOverride && (
            <>
              {/*
                The row's own PANE, spanning the whole strip.
                In the design the takeover is a band, not a floating block: the
                label sits at the left, the channel card sits at its real hours,
                and the tinted ground between them is what says "this row is a
                different kind of thing from the week above it".
              */}
              <Box
                sx={{
                  position: "absolute",
                  left: 0,
                  right: 0,
                  top: OVERRIDE_TOP,
                  height: OVERRIDE_HEIGHT,
                  bgcolor: tokens.brandTintSoft,
                  border: `1px solid ${tokens.brand}`,
                  borderRadius: "8px",
                  opacity: 0.55,
                  pointerEvents: "none",
                }}
              />
              {/*
                Pinned to the left of the VIEWPORT, not of the strip.
                A takeover starts at "now", so its card sits wherever now is -
                halfway across at lunchtime. Without a label anchored where the
                eye starts, the row reads as an unexplained second lane.
              */}
              <Box
                sx={{
                  position: "sticky",
                  left: `${BLOCK_LABEL_EDGE}px`,
                  top: OVERRIDE_TOP,
                  height: OVERRIDE_HEIGHT,
                  width: "fit-content",
                  maxWidth: 260,
                  zIndex: 2,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  px: 1.5,
                  /*
                   * No ground of its own. The label belongs TO the band, and a
                   * white card sitting on a tinted one reads as a second
                   * object with its own edges - which is the opposite of what
                   * the row is: one pane, with words at the left of it.
                   *
                   * It keeps its `zIndex`, so on the rare takeover that starts
                   * at midnight the words still sit above the block rather
                   * than behind it.
                   */
                  pointerEvents: "none",
                }}
              >
                <Typography
                  noWrap
                  sx={{
                    fontSize: 10.5,
                    fontWeight: 700,
                    letterSpacing: "0.6px",
                    textTransform: "uppercase",
                    color: tokens.brand,
                  }}
                >
                  Forced channel active
                </Typography>
                {/*
                  The channel and its end time live HERE, not on the card.
                  On the card they forced a minimum width, and a card wide
                  enough to hold "Shown until 11:37" reached 13:30 on the ruler
                  - the strip contradicting its own caption. In the label they
                  cost the block nothing, so the block can be exactly as wide
                  as the takeover really is.
                */}
                <Typography
                  noWrap
                  sx={{ fontSize: 13, fontWeight: 700, color: tokens.ink }}
                >
                  {playlistName(overrideBlocks[0]!.playlistId)}
                </Typography>
                <Typography noWrap sx={{ fontSize: 11, color: tokens.brand }}>
                  {override && override.until === null
                    ? "Shown until changed back"
                    : `Shown until ${clock(overrideBlocks[0]!.endsAt)}`}
                </Typography>
              </Box>

              {overrideBlocks.map((c) => {
                const left = pctOf(Date.parse(c.startsAt));
                const right = pctOf(Date.parse(c.endsAt));
                const thumb = playlistThumb(c.playlistId);
                return (
                  <Tooltip
                    key={`override-${c.startsAt}-${c.playlistId}`}
                    title={`${playlistName(c.playlistId)} · ${clock(
                      c.startsAt
                    )}–${clock(c.endsAt)} · forced`}
                  >
                    <Box
                      sx={{
                        position: "absolute",
                        left: `${left}%`,
                        /*
                         * EXACTLY its hours. No floor, no minimum.
                         *
                         * The card used to carry the channel name and the end
                         * time, which needed 230px - so a takeover ending at
                         * 11:37 drew a block reaching 13:30 while its own
                         * caption said 11:37. The words moved to the row label
                         * and the block went back to telling the truth.
                         */
                        width: `${Math.max(0.15, right - left)}%`,
                        top: OVERRIDE_TOP,
                        height: OVERRIDE_HEIGHT,
                        borderRadius: "8px",
                        bgcolor: tokens.brandActive,
                        border: `1px solid ${tokens.brand}`,
                        // Marks where the takeover STARTS - the one moment on
                        // this row an operator looks for.
                        borderLeft: `4px solid ${tokens.brand}`,
                        boxShadow: tokens.shadowCard,
                        overflow: "hidden",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      {/*
                        The poster fills the block and is CLIPPED by it. A
                        narrow takeover shows a slice of artwork rather than
                        widening the block to fit a whole one - the block's
                        width is the statement, the picture is decoration.
                      */}
                      <Box
                        {...(thumb
                          ? { component: "img" as const, src: thumb, alt: "" }
                          : {})}
                        sx={{
                          width: OVERRIDE_THUMB,
                          height: OVERRIDE_THUMB,
                          flexShrink: 0,
                          ml: 1,
                          borderRadius: "6px",
                          objectFit: "cover",
                          ...(thumb ? {} : { bgcolor: tokens.divider }),
                        }}
                      />
                    </Box>
                  </Tooltip>
                );
              })}
            </>
          )}

          {/*
            The break ticks are GONE.
            Ten markers an hour drew a picket fence that answered nothing: an
            operator asking about campaigns is asking WHICH ones run WHEN, and
            that is what the lanes below this strip say, a row apiece.
          */}

          {/* Playhead. */}
          {playheadPct >= 0 && playheadPct <= 100 && (
            <Box
              sx={{
                ...PLAYHEAD_SX,
                left: `${playheadPct}%`,
                top: RULER_HEIGHT,
                bottom: 0,
              }}
            />
          )}

          {/* An empty window is a real answer, not a loading state. */}
          {timeline.content.length === 0 && (
            <Typography
              sx={{
                position: "absolute",
                left: 16,
                top: 70,
                fontSize: 13,
                color: tokens.inkMuted,
              }}
            >
              Nothing scheduled in this window.
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
}
